import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

import { addToast } from 'containers/Toasts/actions'

import {
  editTenantApplyError,
  editTenantApplySuccess,
  fetchTenantDataError,
  fetchTenantDataSuccess,
} from './actions'
import {
  DMARC_MANAGEMENT_ADDON_NAME,
  EDIT_TENANT_APPLY_ASYNC,
  EMAIL_DLP_ADDON_NAME,
  EMAIL_ENCRYPTION_ADDON_NAME,
  ENCRYPTION_LEVELS,
  FETCH_TENANT_DATA,
} from './constants'
import { syncUntouchedPlanAddonsForPayload } from './utils'

const fetchTenantDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TENANT_DATA.INITIATE),
    switchMap((action) =>
      api.msp.fetchTenantData(action.payload.tenantId).pipe(
        map((response) => fetchTenantDataSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchTenantDataError, error, [400])))
      )
    )
  )

const editTenantEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EDIT_TENANT_APPLY_ASYNC.INITIATE),
    switchMap((action) => {
      const editTenantState = state$.value.partner.editTenant
      let addonsToEdit = { ...editTenantState.mapSelectedAddons }

      const { enableDmarcForPartnersProps, emailEncryptionProps, ...data } = editTenantState.data
      const touchedAddons = editTenantState.touchedAddons || {}

      // Keep server payload aligned with plan-included locked add-ons,
      // even if they are not user-touched in the current UI flow.
      addonsToEdit = syncUntouchedPlanAddonsForPayload(addonsToEdit, data, touchedAddons)

      // The level card is authoritative for both EE and DLP active flags.
      // Always send both explicitly so switching between levels clears the
      // other flag — prevents EE staying active when DLP is selected and vice versa.
      const hasEeAddon = Object.hasOwn(addonsToEdit, EMAIL_ENCRYPTION_ADDON_NAME)
      const hasDlpAddon = Object.hasOwn(addonsToEdit, EMAIL_DLP_ADDON_NAME)
      const eeChecked = Boolean(addonsToEdit[EMAIL_ENCRYPTION_ADDON_NAME])
      const dlpChecked = Boolean(addonsToEdit[EMAIL_DLP_ADDON_NAME])
      const encryptionChecked = eeChecked || dlpChecked
      const level = emailEncryptionProps?.level

      if (hasEeAddon && hasDlpAddon) {
        addonsToEdit[EMAIL_ENCRYPTION_ADDON_NAME] =
          encryptionChecked && level != null && level !== ENCRYPTION_LEVELS.DLP
        addonsToEdit[EMAIL_DLP_ADDON_NAME] =
          encryptionChecked && level != null && level === ENCRYPTION_LEVELS.DLP
      } else if (hasEeAddon) {
        addonsToEdit[EMAIL_ENCRYPTION_ADDON_NAME] = eeChecked
      } else if (hasDlpAddon) {
        addonsToEdit[EMAIL_DLP_ADDON_NAME] = dlpChecked
      }

      const dmarcEnabled = Boolean(addonsToEdit[DMARC_MANAGEMENT_ADDON_NAME])

      return api.msp
        .editTenant(action.payload.tenantId, {
          ...data,
          dmarcPlan: dmarcEnabled ? data.dmarcPlan : undefined,
          enableDmarcForPartners: enableDmarcForPartnersProps?.visible
            ? enableDmarcForPartnersProps?.value
            : undefined,
          addons: addonsToEdit,
        })
        .pipe(
          switchMap((response) =>
            of(
              editTenantApplySuccess(response),
              addToast({ message: `Tenant ${action.payload.tenantName} was updated successfully!` })
            )
          ),
          catchError((error) => {
            const blockReason = error.response?.data?.blockReason
            const isChurnBlock = blockReason === 'second_churn' || blockReason === 'min_window'
            const apiError =
              error.response?.data?.addons?.[0] ||
              error.response?.data?.non_field_errors?.[0] ||
              error.response?.data?.detail
            const message = isChurnBlock
              ? error.response?.data?.message || 'This change is not allowed.'
              : apiError || (
                  <div>
                    We were unable to update the tenant {action.payload.tenantName}.
                    <br />
                    <br />
                  </div>
                )
            return of(
              epicsErrorHandler(editTenantApplyError, error, [400]),
              addToast({
                message,
                type: 'error',
                ...(isChurnBlock
                  ? {}
                  : {
                      button: {
                        text: 'Contact Customer Success',
                        onClick: () =>
                          window.open(
                            'https://support.ironscales.com/hc/en-us/requests/new',
                            '_blank'
                          ),
                      },
                    }),
              })
            )
          })
        )
    })
  )

export default combineEpics(fetchTenantDataEpic, editTenantEpic)
